import { useContext, useMemo } from 'react';

import { sortTags } from '@libs/NwUtils';

import { ListContext } from '@contexts/ListContext';

/**
 * Catalogo tag condiviso per un tipo di oggetto (Model, Customer, Playlist, Document, News, ...).
 * Unico punto da cui TagPicker, InputTag e ActionToolbar leggono publictags/privatetags.
 *
 * Non usa mapTags() per due motivi: quello scarta UsageCount, che serve al picker, e
 * abbassa il case di Name. Qui Name resta com'e' nel catalogo perche' e' quello che si vede:
 * il minuscolo e' solo presentazione, lo applica il pannello. I filtri per tag confrontano i
 * nomi senza distinzione di maiuscole, in matchesTagFilter.
 * L'ordinamento resta sortTags(), lo stesso di News e del wizard di import che
 * continuano a passare da mapTags.
 *
 * Il confronto su TagTypeName e' esatto e case-sensitive, come in mapTags().
 * `isPrivate` deriva dalla lista di provenienza e non da un campo del tag: le API di lettura
 * non garantiscono PrivacyLevel nella risposta.
 */
const useTagCatalog = (itemType, { publicOnly = false } = {}) => {
    const { cachedList } = useContext(ListContext);
    const publicTags = cachedList.publictags.items;
    const privateTags = cachedList.privatetags.items;

    return useMemo(() => {
        const mapForType = (tags, isPrivate) =>
            tags
                .filter((tag) => tag.TagTypeName === itemType)
                .map((tag) => ({
                    ID: tag.ID,
                    Name: tag.Name,
                    UsageCount: tag.UsageCount,
                    isPrivate,
                }));

        const catalog = [...mapForType(publicTags, false), ...(publicOnly ? [] : mapForType(privateTags, true))];

        // sortTags muta l'array che riceve: qui e' sempre un array nuovo, quindi e' sicuro
        return sortTags(catalog);
    }, [publicTags, privateTags, itemType, publicOnly]);
};

export const useAdjustTagUsageCount = () => {
    const { AdjustTagUsageCount } = useContext(ListContext);
    return AdjustTagUsageCount;
};

export default useTagCatalog;
